import { useState, useCallback, useMemo, useContext, useRef, useEffect } from 'react'
import { useUser } from '@fs/zion-user'
import { trackEvent } from '@fs/zion-analytics'
import { fetchReservations, useReservationListPids } from '../service/reservations'
import { CountsContext } from './useReservationCounts'
import removeDuplicateSS from '../lib/removeDuplicateSS'

export default function useReservations({ isSharedWithTemple, hasVisitedPageToMakeCall } = {}) {
  const makeCall = hasVisitedPageToMakeCall?.current
  const user = useUser()
  const {
    chunkedPids: origChunkedPids,
    refetch,
    error: pidsError,
    loading: loadingPids,
  } = useReservationListPids(makeCall && user?.cpUserId, isSharedWithTemple)

  const [reservations, setReservations] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasFetched, setHasFetched] = useState(false)
  const [reservationsError, setReservationsError] = useState(null)
  const { refetchCounts } = useContext(CountsContext)
  const timesRecalculated = useRef(0)
  const isCurrent = useRef(true)

  const updateReservations = useCallback(
    async ({ chunkedPids }) => {
      setIsLoading(true)

      if (!user?.cpUserId) return

      if (!chunkedPids || chunkedPids.length === 0) {
        setHasFetched(true)
        setIsLoading(false)
        return
      }
      try {
        const { data: rows, recalculate } = await fetchReservations({
          pids: chunkedPids,
          isSharedWithTemple,
          cpUserId: user?.cpUserId,
        })

        const additionalRows = splitOrdinancesByReserveOwner(rows)

        const newRows = removeDuplicateSS([...rows, ...additionalRows])
        // check if any are calculating
        const calculatingRows = newRows.filter(({ calculating }) => calculating)
        // Only recalculate up to 3 times max, which is ~6 seconds
        if (recalculate && calculatingRows.length > 0 && timesRecalculated.current < 3) {
          timesRecalculated.current++
          setTimeout(() => {
            // we need to keep the context of all the pids ever requested since we WILL NOT be able to choose the correct rows to keep or not keep,
            // with out the full context in a calculating states
            updateReservations({ chunkedPids })
          }, 2000)
        } else {
          if (timesRecalculated.current >= 3) {
            trackEvent({ event_name: 'Not Able to Recalculate Ordinances in Time' })
          }
          timesRecalculated.current = 0
          if (isCurrent.current) {
            setReservations((prev) => {
              const existingRowsToKeep = chooseRowsToKeep(prev, newRows, chunkedPids)
              return [...existingRowsToKeep, ...newRows]
            })
            setIsLoading(false)
            setHasFetched(true)
            refetchCounts()
          }
        }
      } catch (e) {
        setIsLoading(false)
        setReservationsError(e)
      }
    },
    [isSharedWithTemple, refetchCounts, user?.cpUserId]
  )

  useEffect(() => {
    isCurrent.current = true

    return () => {
      isCurrent.current = false
    }
  }, [])

  useEffect(() => {
    updateReservations({ chunkedPids: origChunkedPids })
  }, [origChunkedPids, updateReservations])

  return useMemo(
    () => ({
      reservations,
      isLoading: isLoading || loadingPids,
      updateReservations,
      error: pidsError || reservationsError,
      hasFetched,
      fetchReservationList: refetch,
    }),
    [hasFetched, isLoading, loadingPids, pidsError, refetch, reservations, reservationsError, updateReservations]
  )
}

function splitOrdinancesByReserveOwner(rows) {
  const additionalRows = []

  rows?.forEach((row) => {
    const rowsAdded = {}
    const currentUserId = row.cpUserId
    Object.entries(row.ordinances).forEach(([ordinanceType, ordinance]) => {
      const secondaryReserverId = ordinance.alternateReservationDetails?.ownerId
      if (secondaryReserverId && secondaryReserverId !== currentUserId && ordinance.groupId) {
        if (rowsAdded[secondaryReserverId]) {
          rowsAdded[secondaryReserverId].push(ordinanceType)
        } else {
          rowsAdded[secondaryReserverId] = [ordinanceType]
        }
      }
    })

    Object.values(rowsAdded).forEach((ordinanceTypesArray) => {
      if (!allOrdinancesReservedTwice(row, ordinanceTypesArray)) {
        const onlySecondaryReservedOrdinances = { ...row }
        onlySecondaryReservedOrdinances.ordinances = {}
        ordinanceTypesArray.forEach((ordinanceKey) => {
          onlySecondaryReservedOrdinances.ordinances[ordinanceKey] = row.ordinances[ordinanceKey]
          // remove the secondary reservations from the original row
          delete row.ordinances[ordinanceKey]
        })
        if (!ordinanceTypesArray.includes('sealingToParents')) {
          onlySecondaryReservedOrdinances.relationships = []
        }
        additionalRows.push(onlySecondaryReservedOrdinances)
      }
    })
  })
  return additionalRows
}

function allOrdinancesReservedTwice(row, addedOrdinanceTypesArray) {
  let displayedOrdinances = {}
  Object?.entries(row.ordinances).forEach(([ordinanceType, singleOrdinance]) => {
    // will only show ordinances that the user owns.
    // This will only effect the rows rendered not the ordinances rendered
    if (singleOrdinance.owner === row.cpUserId) {
      displayedOrdinances[ordinanceType] = singleOrdinance
    }
  })
  if (Object.keys(displayedOrdinances).length === 0) {
    displayedOrdinances = null
  }
  if (row.type === 'sealingToSpouse') {
    return addedOrdinanceTypesArray.length === 1
  }
  return (
    addedOrdinanceTypesArray.length ===
    Object.keys(displayedOrdinances || row.ordinances).filter((ord) => ord !== 'sealingToSpouse').length
  )
}

function chooseRowsToKeep(reservations, rows, chunkedPids) {
  return reservations.filter((row) => {
    // if we have new data the matches the old data then we dont want to include the old data
    // if there is no new data but old data and we requested new data for the pid then we dont want to include the old data
    return (
      !rows.some((newRow) => newRow.type === row.type && newRow.person.id === row.person.id) &&
      !chunkedPids.some((pidArray) => pidArray.includes(row.person.id))
    )
  })
}
